/**
 * WI-workos-01 (N1) — WorkOS AuthKit login configuration + tenant redirect-URI
 * registration, authored against a MOCK AuthKit surface (fixture leg; the live
 * AuthKit round-trip is GATED §S8, not attempted here).
 *
 * Derives-from: architecture §1 Plane 1 (AuthKit hosted-login; the OIDC token
 * carries `iss=WorkOS, org_id, role`); DEC-0002 §Decision(1); spec §1.1.
 * Satisfies: AC-workos-01 (a human completes AuthKit hosted login into a tenant;
 * the returned token carries `iss=WorkOS`, `org_id`, `role`; a user with no
 * membership in that org cannot obtain a token).
 * Respects: TW-workos-01 — NO WorkOS credential appears inline here. The AuthKit
 * client_id is a PUBLIC identifier (safe to ship to the browser); the API key /
 * client secret is a RUNTIME secret handled ONLY by WI-workos-03 (SOPS+age).
 *
 * This module is deliberately framework-agnostic (no middleware pre-decided —
 * DS-workos-03 stays OPEN, resolved minimally-for-fixture in workos-verify.ts).
 */

/** WorkOS's fixed issuer string, stamped onto every AuthKit-issued token
 * (architecture §1 Plane 1: `iss=WorkOS`). PINNED here so the verifier
 * (WI-workos-07) checks against a single source of truth. */
export const WORKOS_ISSUER = "WorkOS";

/** The pilot tenant login-callback redirect URIs registered with AuthKit.
 * The wildcard `*.app.buildengine.ai → app ingress` is the LOCKED parent stamp's
 * job (consumed, not re-spec'd); this is the per-app callback path AuthKit
 * redirects to after a hosted login. Localhost is the dev-server callback. */
export const REDIRECT_URIS = {
  /** Pilot tenant (Mercer) production callback. */
  mercer: "https://mercer.app.buildengine.ai/auth/callback",
  /** Local Vite dev-server callback (port 5173, the PWA dev default). */
  localhost: "http://localhost:5173/auth/callback",
} as const;

/** The AuthKit login configuration the app boots with. `client_id` is PUBLIC.
 * The API key is NOT here — it is a runtime secret (WI-workos-03). */
export interface AuthKitConfig {
  /** PUBLIC AuthKit client id (safe in the browser bundle). NEVER the API key. */
  client_id: string;
  /** WorkOS's hosted-login authorize endpoint (AuthKit). */
  authorize_url: string;
  /** Registered redirect URI for this environment. Must be one of REDIRECT_URIS. */
  redirect_uri: string;
}

/** Fixture AuthKit config for the dry leg. `client_id` uses the obviously-fake
 * test convention (see TW-workos-01 / the WI-workos-10 secret scan) — a public
 * id, not a credential, but kept fake so nothing real can leak from a fixture. */
export const FIXTURE_AUTHKIT_CONFIG: AuthKitConfig = {
  client_id: "client_test_FAKE_authkit_pilot",
  authorize_url: "https://api.workos.com/user_management/authorize",
  redirect_uri: REDIRECT_URIS.mercer,
};

/** Validates that a redirect URI is one of the registered set — the app must
 * never send AuthKit an unregistered callback (an open-redirect / phishing
 * vector). Returns true only for a URI in REDIRECT_URIS. */
export function isRegisteredRedirectUri(uri: string): boolean {
  return Object.values(REDIRECT_URIS).includes(uri as (typeof REDIRECT_URIS)[keyof typeof REDIRECT_URIS]);
}
